"use client";

import { truncateAddress } from "@aptos-labs/wallet-adapter-react";
import { Button } from "@decibeltrade/web-core/components";
import { ModalContent, type ModalRenderProps, openModal } from "@decibeltrade/web-core/modal";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { css } from "styled-system/css";
import { stack } from "styled-system/patterns";

import { useViewAs } from "@/contexts/view-as-context";
import { addressesEqual } from "@/utils/address";

import { Clickable } from "../clickable";
import { FormField } from "../form/form-field";
import { Input } from "../form/input";
import { ModalCloseButton } from "./modal-close-button";

const VIEW_AS_MODAL_ID = "view-as-modal";
const RECENT_ADDRESSES_KEY = "view-as-recent";
const MAX_RECENT = 5;

function getRecentAddresses(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_ADDRESSES_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

function saveRecentAddress(address: string) {
  const recent = getRecentAddresses().filter((a) => !addressesEqual(a, address));
  recent.unshift(address);
  localStorage.setItem(RECENT_ADDRESSES_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
}

export function openViewAsModal() {
  openModal({
    id: VIEW_AS_MODAL_ID,
    renderContent: (modalProps) => <ViewAsModal {...modalProps} />,
  });
}

function ViewAsModal({ close }: ModalRenderProps) {
  const t = useTranslations("viewAsUser");
  const { activateViewAs } = useViewAs();
  const [addressInput, setAddressInput] = useState("");
  const [recentAddresses] = useState(() => getRecentAddresses());

  const handleActivate = (address: string) => {
    const trimmed = address.trim();
    if (!trimmed) return;
    saveRecentAddress(trimmed);
    activateViewAs(trimmed);
    close();
  };

  return (
    <ModalContent className={css({ w: "[425px]", p: "16" })}>
      <ModalCloseButton close={close} />
      <div className={stack({ gap: "20" })}>
        <div className={stack({ gap: "12" })}>
          <h2 className={css({ textStyle: "heading.3xs", color: "text.primary", pt: "12" })}>
            {t("viewAsUser")}
          </h2>
          <p className={css({ textStyle: "body.md", color: "text.tertiary" })}>
            {t("viewAsDescription")}
          </p>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleActivate(addressInput);
          }}
          className={stack({ gap: "8" })}
        >
          <FormField label={t("walletAddress")}>
            {(formControlProps) => (
              <Input
                {...formControlProps}
                value={addressInput}
                onChange={(e) => setAddressInput(e.target.value)}
                placeholder="0x..."
              />
            )}
          </FormField>
          <Button
            type="submit"
            variant="primary"
            size="md"
            fullWidth
            disabled={!addressInput.trim()}
          >
            {t("viewAs")}
          </Button>
        </form>

        {recentAddresses.length > 0 && (
          <div className={stack({ gap: "8" })}>
            <span className={css({ textStyle: "body.md", color: "text.tertiary" })}>
              {t("recentAddresses")}
            </span>
            {recentAddresses.map((addr) => (
              <Clickable
                key={addr}
                onClick={() => handleActivate(addr)}
                className={css({
                  bg: "background.canvas",
                  border: "1px solid",
                  borderColor: "border.primary",
                  borderRadius: "200",
                  px: "12",
                  py: "8",
                  color: "text.secondary",
                  textStyle: "body.md",
                  textAlign: "left",
                  _hover: { borderColor: "border.tertiary", color: "text.primary" },
                  transitionPreset: "colors",
                })}
                title={addr}
              >
                {truncateAddress(addr)}
              </Clickable>
            ))}
          </div>
        )}
      </div>
    </ModalContent>
  );
}
